<template>
  <transition
    :enterActiveClass="$style.gettingStartedPanelTransition"
    :enterClass="$style.gettingStartedPanelEnter"
    :leaveActiveClass="$style.gettingStartedPanelTransition"
    :leaveToClass="$style.gettingStartedPanelLeaveTo"
  >
    <div
      v-if="settings"
      v-show="visible"
      :class="$style.container"
    >
      <component
        :is="gettingStartedGraphic.component"
        :class="graphicClass"
        data-test-id="GettingStartedPanel__gettingStartedGraphic"
        :src="gettingStartedGraphic.src"
      />

      <h2
        :class="$style.header"
        data-test-id="GettingStartedPanel__headerText"
      >
        <span
          :style="iconNameStyle"
        >
          <Icon
            v-if="iconName"
            :icon="iconName"
          />
        </span>
        {{ settings.headerText }}
      </h2>
      <MessageContainer
        v-if="settings.warningMessage"
        :class="$style.messageContainer"
        :level="warn"
      >
        <MarkdownInline
          :mdTemplate="settings.warningMessage.text"
        />
        <ExternalLink
          v-if="settings.warningMessage.link"
          :href="settings.warningMessage.link"
          :showExternalLinkIcon="showLearnMoreExternalIcon"
          text="Learn More"
        />
      </MessageContainer>
      <p
        :class="[$style.mainText, {[$style.mdmMainText] : isMdmPanel}]"
      >
        <template v-for="(item, index) in settings.mainText">
          <p
            :key="index"
          >
            <MarkdownInline
              data-test-id="no-text-component"
              :mdTemplate="item.text"
            />
            <ExternalLink
              v-if="item.link"
              data-test-id="GettingStartedPanel__learnMoreLink"
              :href="item.link.href"
              :showExternalLinkIcon="showLearnMoreExternalIcon"
              :text="item.link.text"
              @click="handleLearnMoreLinkClick(item.link)"
            />
          </p>
        </template>
      </p>
      <WaysToAddAppleAppCards
        v-if="showAppleVppsCards"
        :addAppleAppCards="settings.waysToAddCards"
      />
      <WaysToAddSystemUserCards
        v-if="showWayToAddSystemUserCards"
        :addSystemUserCards="settings.waysToAddCards.filter(card => !card.hide)"
        :itemtype="itemType"
      />
      <div v-else-if="showDeviceCards">
        <WaysToAddDeviceCards
          :addDeviceCards="settings.waysToAddCards"
          :itemtype="itemType"
        />
        <SimulationWidget
          :class="$style.resourcesWidget"
          :itemType="itemType"
        />
      </div>
      <TemplateProtipBanner
        v-else-if="settings.proTip"
      >
        <span>
          {{ settings.proTip.text }}
          <ExternalLink
            v-if="settings.proTip.link"
            :href="settings.proTip.link.href"
            :showExternalLinkIcon="false"
            :text="settings.proTip.link.text"
            @click="() => handleProtipSegmentEvent(settings.proTip.link.segmentEvent)"
          />
        </span>
      </TemplateProtipBanner>
      <FlatActionButton
        v-if="buttonLabel"
        :action="ctaButtonAction"
        :class="$style.buttonCta"
        data-test-id="GettingStartedPanel__CtaButton"
        :text="buttonLabel"
      />
    </div>
  </transition>
</template>

<script>
import {
  Icon,
  MarkdownInline,
} from '@jumpcloud/ui-components';
import { events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { isEmpty, values } from 'lodash';
import { mapState } from 'vuex';
import Constants, { ItemTypeConsts } from 'client/util/Constants';
import EventManager from 'client/GrowthEngineering/EventManager';
import ExternalLink from 'components/ExternalLink.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import GettingStartedGraphics from 'client/components/GettingStartedPanel/GettingStartedGraphics';
import GettingStartedPanelSettings from 'client/components/GettingStartedPanel/GettingStartedPanelSettings';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';
import SimulationWidget from 'client/components/GettingStartedPanel/SimulationWidget.vue';
import TemplateProtipBanner from 'client/components/ProtipBanner/TemplateProtipBanner.vue';
import WaysToAddAppleAppCards from 'client/MDM/WaysToAddAppleAppsCards/WaysToAddAppleAppCards.vue';
import WaysToAddDeviceCards from 'client/systems/WaysToAddDeviceCards/WaysToAddDeviceCards.vue';
import WaysToAddSystemUserCards from 'client/systemusers/WaysToAddSystemUserCards/WaysToAddSystemUserCards.vue';

const {
  device,
  hrDirectories,
  hrDirectory,
  mdmSettings,
  sso,
  systemUser,
  vppApps,
} = ItemTypeConsts;

const { warn } = Constants;

export default {
  name: 'GettingStartedPanel',

  components: {
    ExternalLink,
    FlatActionButton,
    SimulationWidget,
    Icon,
    MarkdownInline,
    MessageContainer,
    TemplateProtipBanner,
    WaysToAddAppleAppCards,
    WaysToAddDeviceCards,
    WaysToAddSystemUserCards,
  },

  props: {
    buttonLabel: {
      type: String,
      default: '',
    },
    customGettingStartedGraphic: {
      type: Object,
      default: () => {},
    },
    iconName: {
      type: String,
      default: '',
    },
    iconNameStyle: {
      type: Object,
      default: () => {},
    },
    itemType: {
      type: String,
      required: true,
      validator: function validator(value) {
        return values(ItemTypeConsts).indexOf(value) !== -1;
      },
    },
    // Meant to use only for when listening to the event is
    // harder than sending the callback function
    // e.g. inside a dynamic component
    onCtaButtonClick: {
      type: Function,
      default: () => {},
    },
    showLearnMoreExternalIcon: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Function,
      default: () => {},
    },
    visible: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      warn,
    };
  },

  computed: {
    ...mapState('FeatureFlagsModule', {
      bambooRealTimeImport: state => state.betaFeatures.bambooRealTimeImport,
      navBarSsoExperiment: state => state.betaFeatures.navBarSsoExperiment,
    }),

    gettingStartedGraphic() {
      if (!isEmpty(this.customGettingStartedGraphic)) {
        return this.customGettingStartedGraphic;
      }
      if (this.itemType === hrDirectory && this.bambooRealTimeImport) {
        return GettingStartedGraphics[hrDirectories];
      }
      return GettingStartedGraphics[this.itemType] || GettingStartedGraphics.default;
    },

    graphicClass() {
      const classes = [this.$style.graphic];
      const itemTypesWithSmallGraphics = [
        ItemTypeConsts.systemUser,
      ];
      const itemTypesWithMediumGraphics = [
        ItemTypeConsts.application,
        ItemTypeConsts.group,
        ItemTypeConsts.radiusServer,
        ItemTypeConsts.device,
      ];
      const itemTypesWithDynamicGraphics = [
        ItemTypeConsts.mdmSettings,
      ];
      if (itemTypesWithSmallGraphics.indexOf(this.itemType) > -1) {
        classes.push(this.$style.graphicSmall);
      }
      if (itemTypesWithMediumGraphics.indexOf(this.itemType) > -1) {
        classes.push(this.$style.graphicMedium);
      }
      if (itemTypesWithDynamicGraphics.indexOf(this.itemType) > -1) {
        classes.push(this.$style.graphicDynamic);
      }
      return classes;
    },

    isMdmPanel() {
      return this.itemType === mdmSettings;
    },

    settings() {
      if (
        this.itemType === hrDirectory
        || this.itemType === systemUser
        || this.itemType === sso
      ) {
        return GettingStartedPanelSettings[this.itemType]({
          bambooRealTimeImport: this.bambooRealTimeImport,
          navBarSsoExperiment: this.navBarSsoExperiment,
        });
      }
      return GettingStartedPanelSettings[this.itemType];
    },

    showAppleVppsCards() {
      return this.itemType === vppApps && this.settings.waysToAddCards;
    },

    showDeviceCards() {
      return this.itemType === device && this.settings.waysToAddCards;
    },

    showWayToAddSystemUserCards() {
      return this.itemType === systemUser
        && GettingStartedPanelSettings[this.itemType]({
          bambooRealTimeImport: this.bambooRealTimeImport,
        }).waysToAddCards;
    },
  },

  watch: {
    visible: {
      // When the getting started panel is not visible, show the tooltip
      handler(visible) {
        this.showTooltip(!visible);
      },
      immediate: true,
    },
  },

  created() {
    this.checkItemTypeCustomContent();
  },

  methods: {
    checkItemTypeCustomContent() {
      if (this.itemType === device || this.itemType === systemUser) {
        this.emitItemTypeCustomContentEvent();
      }
    },

    ctaButtonAction() {
      this.onCtaButtonClick();
      this.$emit('ctaButtonClick');
    },

    emitItemTypeCustomContentEvent() {
      this.$emit('item-type-custom-content-active');
    },

    handleLearnMoreLinkClick(itemLink) {
      EventManager.triggerEvent(events.gettingStartedPageLinkClicked, {
        itemType: this.itemType,
        linkUrl: itemLink.href,
        linkText: itemLink.text,
      });
    },

    handleProtipSegmentEvent(segmentEvent) {
      if (segmentEvent && typeof segmentEvent === 'function') {
        segmentEvent();
      }
    },
  },
};
</script>

<style module>
@value (
  max-width-small,
  max-width-medium,
  max-width-large,
  max-width-x-large) from '~css/breakpoints.css';
@value (gradientLight2) from '~css/colors.css';
@value (fadeTransition) from '~css/transitions.css';
@value (gettingStartedPanelZIndex) from '~css/z-index.css';

.container {
  background: gradientLight2;
  padding: 60px 20px 0;
  text-align: center;
  width: 100%;
}

/* @todo These styles should be removed when Applications and Commands
are refactored into Vue */

:global(.item-list) ~ .container {
  position: absolute;
  top: 80px;
}

:global(.command-result) ~ .container {
  height: 100%;
}

.graphic {
  max-width: 350px;
}

.graphic.graphicSmall {
  max-width: 250px;
  width: 50%;
}

.graphic.graphicMedium { max-width: 350px; }

.graphic.graphicDynamic { max-width: 750px; }

@media (max-width: max-width-x-large) {
  .graphic.graphicDynamic { max-width: 80%; }
}

.header {
  font-size: 24px !important;
  font-weight: bold !important;
  margin-top: 40px !important;
}

.mainText {
  display: inline-block;
  font-size: 16px;
  line-height: 1.7;
  margin-top: 15px !important;
  max-width: 750px;
}

.mainText a:focus,
.mainText a:hover {
  text-decoration: none !important;
}

.messageContainer {
  margin: auto;
  width: 40%;
}

.ctaText {
  font-size: 21px;
  font-weight: bold;
  margin-top: 25px !important;
}

.mainActionButton {
  cursor: default !important;
  margin: 0 4px !important;
  position: relative;
  top: -3px;
  vertical-align: middle;
}

.mdmMainText {
  text-align: left;
}

.gettingStartedPanelEnter,
.gettingStartedPanelLeaveTo {
  opacity: 0;
}

.gettingStartedPanelTransition {
  transition: fadeTransition;
}

.container .buttonCta {
  display: block;
  margin: 0 auto;
}

.resourcesWidget {
  margin: 0 auto;
  padding: var(--jc-spacer-large) 0;
  text-align: left;
  width: 80%;
}

@media (max-width: max-width-large) {
  .container { top: 90px; }
}

@media (max-width: max-width-medium) {
  .container { padding-top: 50px; }
}

@media (max-width: max-width-small) {
  .header { font-size: 20px !important; }

  .mainText {
    font-size: 15px;
    margin-top: 15px !important;
  }

  .ctaText {
    font-size: 18px;
    margin-top: 30px !important;
  }
}
</style>
